import { cookie } from 'redux-effects-universal-cookie';
import { reduce, mergeWith, find, path, omit, forEach, assocPath, over, lensPath, inc, pathOr, append, isEmpty, reject, isNil } from 'ramda';

var AUTHENTICATED = 'analytics/AUTHENTICATED';
var CRYPTO_CURRENCY_SELECTED = 'analytics/CRYPTO_CURRENCY_SELECTED';
var DEPOSIT_AMOUNT_SELECTED = 'analytics/DEPOSIT_AMOUNT_SELECTED';
var DEPOSIT_PAYMENT_METHOD_SELECTED = 'analytics/DEPOSIT_PAYMENT_METHOD_SELECTED';
var DEPOSIT_TRANSACTION_COMPLETED = 'analytics/DEPOSIT_TRANSACTION_COMPLETED';
var DEPOSITED = 'analytics/DEPOSITED';
var PAGE_VIEWED = 'analytics/PAGE_VIEWED';
var REGISTERED = 'analytics/REGISTERED';
var BONUS_CLAIMED = 'analytics/BONUS_CLAIMED';
var REGISTRATION_STARTED = 'analytics/REGISTRATION_STARTED';
var PLAY_FOR_FUN = 'analytics/PLAY_FOR_FUN';
var OPEN_GAME = 'analytics/OPEN_GAME';

var BTAG_UPDATED = 'analytics/BTAG_UPDATED';
var CLICK_ID_UPDATED = 'analytics/CLICK_ID_UPDATED';
var REFERRAL_ID_UPDATED = 'analytics/REFERRAL_ID_UPDATED';
var SUB_ID_UPDATED = 'analytics/SUB_ID_UPDATED';
var TWITTER_CLICK_ID_UPDATED = 'analytics/TWITTER_CLICK_ID_UPDATED';
var UTM_CAMPAIGN_UPDATED = 'analytics/UTM_CAMPAIGN_UPDATED';
var UTM_MEDIUM_UPDATED = 'analytics/UTM_MEDIUM_UPDATED';
var UTM_SOURCE_UPDATED = 'analytics/UTM_SOURCE_UPDATED';
var WEBMASTER_ID_UPDATED = 'analytics/WEBMASTER_ID_UPDATED';
var CLEARED = 'analytics/CLEARED';

var EVENT_QUEUED = 'analytics/EVENT_QUEUED';
var EVENT_SKIPPED = 'analytics/EVENT_SKIPPED';
var EVENT_SENT = 'analytics/EVENT_SENT';

var BTAG = 'BTAG';
var CLICK_ID = 'CLICK_ID';
var REFERRAL_ID = 'REFERRAL_ID';
var SUB_ID = 'SUB_ID';
var TWITTER_CLICK_ID = 'TWITTER_CLICK_ID';
var UTM_CAMPAIGN = 'UTM_CAMPAIGN';
var UTM_MEDIUM = 'UTM_MEDIUM';
var UTM_SOURCE = 'UTM_SOURCE';
var WEBMASTER_ID = 'WEBMASTER_ID';

/* istanbul ignore next: third-party workaround */
function normalizeCookieOptions(options) {
  var normalizedOptions = Object.assign({}, options);
  if (process.browser) {
    if (options.maxAge) {
      // component-cookie expect property name to be lowercased
      normalizedOptions.maxage = options.maxAge;
      delete normalizedOptions.maxAge;
    }
  }
  return normalizedOptions;
}

function updateCookie(name, value, options) {
  if (value) {
    return cookie(name, value, normalizeCookieOptions(Object.assign({
      maxAge: 1000 * 60 * 60 * 24 * 7,
      path: '/',
      httpOnly: false
    }, options)));
  }
}

/**
 * Queue event to be reported when data is ready
 *
 * @param {Function} event Redux Beacon event definition creator
 */
function queueEvent(event) {
  return {
    type: EVENT_QUEUED,
    payload: event
  };
}

/**
 * Mark event as sent
 *
 * @private
 * @param {number} id
 */
function sendEvent(id) {
  return {
    type: EVENT_SENT,
    payload: id
  };
}

/**
 * Mark event as skipped (e.g. when retry limit is reached)
 *
 * @private
 * @param {number} id
 */
function skipEvent(id) {
  return {
    type: EVENT_SKIPPED,
    payload: id
  };
}

function authenticate() {
  return queueEvent({ type: AUTHENTICATED });
}

/**
 * @param {number} amountCents Deposit amount in cents
 */
function selectAmount(amountCents) {
  return queueEvent({
    type: DEPOSIT_AMOUNT_SELECTED,
    payload: amountCents
  });
}

/**
 * @param {string} depositMethod selected deposit method, e.g 'PiqCreditcardDeposit'
 */
function selectPaymentMethod(depositMethod) {
  return queueEvent({
    type: DEPOSIT_PAYMENT_METHOD_SELECTED,
    payload: depositMethod
  });
}

/**
 * @param {string} cryptoCurrency selected crypto currency, e.g 'UBT'
 */
function selectCryptoCurrency(cryptoCurrency) {
  return queueEvent({
    type: CRYPTO_CURRENCY_SELECTED,
    payload: cryptoCurrency
  });
}

/**
 * @param {string} transactionStatus 'successful' | 'failed' | 'cancelled'
 * @param {string} depositMethod selected deposit method, e.g 'PiqCreditcardDeposit'
 */
function completeTransaction(transactionStatus, depositMethod) {
  var validStatus = ['successful', 'failed', 'cancelled'];

  if (!validStatus.includes(transactionStatus)) {
    throw new Error('\'transactionStatus\' must be one of the following strings: ' + validStatus.join(', '));
  }
  return queueEvent({
    type: DEPOSIT_TRANSACTION_COMPLETED,
    payload: { transactionStatus: transactionStatus, depositMethod: depositMethod }
  });
}

function deposit(amount) {
  return queueEvent({
    type: DEPOSITED,
    payload: amount
  });
}

function pageview(path$$1) {
  return queueEvent({
    type: PAGE_VIEWED,
    payload: path$$1
  });
}

function playForFun(game) {
  return queueEvent({
    type: PLAY_FOR_FUN,
    payload: game
  });
}

function openGame(game) {
  return queueEvent({
    type: OPEN_GAME,
    payload: game
  });
}

function bonus(params) {
  return queueEvent({
    type: BONUS_CLAIMED,
    payload: {
      claimed: params.claimed,
      value: params.value
    }
  });
}

function register(registrationType) {
  return queueEvent({
    type: REGISTERED,
    payload: registrationType
  });
}

function registrationStarted(registrationType) {
  return queueEvent({
    type: REGISTRATION_STARTED,
    payload: registrationType
  });
}

/**
 * Updates btag state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} btag
 * @returns {Array.<Object>} Redux actions
 */
function updateBtag(btag) {
  return [{
    type: BTAG_UPDATED,
    payload: btag
  }, updateCookie(BTAG, btag)];
}

/**
 * Updates click_id state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} clickId
 * @returns {Array.<Object>} Redux actions
 */
function updateClickId(clickId) {
  return [{
    type: CLICK_ID_UPDATED,
    payload: clickId
  }, updateCookie(CLICK_ID, clickId)];
}

/**
 * Updates referral_id state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} referralId
 * @returns {Array.<Object>} Redux actions
 */
function updateReferralId(referralId) {
  return [{
    type: REFERRAL_ID_UPDATED,
    payload: referralId
  }, updateCookie(REFERRAL_ID, referralId)];
}

/**
 * Updates webmaster_id state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} webmasterId
 * @returns {Array.<Object>} Redux actions
 */
function updateWebmasterId(webmasterId) {
  return [{
    type: WEBMASTER_ID_UPDATED,
    payload: webmasterId
  }, updateCookie(WEBMASTER_ID, webmasterId)];
}

/**
 * Updates sub_id state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} subId
 * @returns {Array.<Object>} Redux actions
 */
function updateSubId(subId) {
  return [{
    type: SUB_ID_UPDATED,
    payload: subId
  }, updateCookie(SUB_ID, subId)];
}

/**
 * Updates utm_campaign state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} utmCampaign
 * @returns {Array.<Object>} Redux actions
 */
function updateUtmCampaign(utmCampaign) {
  return [{
    type: UTM_CAMPAIGN_UPDATED,
    payload: utmCampaign
  }, updateCookie(UTM_CAMPAIGN, utmCampaign)];
}

/**
 * Updates utm_medium state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} utmMedium
 * @returns {Array.<Object>} Redux actions
 */
function updateUtmMedium(utmMedium) {
  return [{
    type: UTM_MEDIUM_UPDATED,
    payload: utmMedium
  }, updateCookie(UTM_MEDIUM, utmMedium)];
}

/**
 * Updates utm_source state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} utmSource
 * @returns {Array.<Object>} Redux actions
 */
function updateUtmSource(utmSource) {
  return [{
    type: UTM_SOURCE_UPDATED,
    payload: utmSource
  }, updateCookie(UTM_SOURCE, utmSource)];
}

/**
 * Updates twclid state and store it in a cookie
 * @memberof rushplay/analytics
 * @param {string} utmSource
 * @returns {Array.<Object>} Redux actions
 */
function updateTwitterClickId(twclid) {
  return [{
    type: TWITTER_CLICK_ID_UPDATED,
    payload: twclid
  }, updateCookie(TWITTER_CLICK_ID, twclid)];
}
/**
 * Clears state and all the tracking cookies
 * @memberof rushplay/analytics
 * @returns {Array.<Object>} Redux actions
 */
function clear() {
  return [{
    type: CLEARED
  }, cookie(BTAG, null), cookie(CLICK_ID, null), cookie(REFERRAL_ID, null), cookie(SUB_ID, null), cookie(TWITTER_CLICK_ID, null), cookie(UTM_CAMPAIGN, null), cookie(UTM_MEDIUM, null), cookie(UTM_SOURCE, null), cookie(WEBMASTER_ID, null)];
}

/* eslint-disable no-empty */

/**
 * Number of attempts to dispatch queued event.
 *
 * @constant
 */
var SKIP_LIMIT = 10000;

/**
 * Combine array of event definitions into single event definitions object
 *
 * Merging is done by key (action type), output is the function applying event
 * creators from right to left until one of them returns non-null definition.
 *
 * @param {Array.<Object>} eventDefinitions
 * @return {Object.<string, Function>}
 */
function combineEventDefinitions(eventDefinitions) {
  return reduce(mergeWith(function (left, right) {
    return function () {
      if (right.apply(undefined, arguments) == null) {
        return null;
      }

      return left.apply(undefined, arguments);
    };
  }), {}, eventDefinitions);
}

/**
 * Middleware to buffer event dispatching until event definition could be
 * created.
 *
 * Returning `null` instead of event definition from event creator will keep
 * event in queue. This helps to collect data asynchronously if needed to
 * prevent dispatching malformed events.
 *
 * @param {Object} options
 * @param {Array.<Object>} options.events
 *   Array of Redux Beacon event definitions. Will be combined in single event
 *   definition by `combineEventDefinitions`.
 * @param {Function} options.queueSelector
 *   Redux selector returning event queue when given full Redux state.
 * @param {Function} options.onError
 *   Error handler callback
 *
 * @returns {Function} Redux middleware
 */
function eventQueuingMiddleware(options) {
  var events = combineEventDefinitions(options.events);
  var getQueue = options.queueSelector;
  var onError = options.onError || function () {};

  return function (store) {
    return function (next) {
      return function (action) {
        switch (action.type) {
          case EVENT_SENT:
          case EVENT_SKIPPED:
            {
              return next(action);
            }

          // `EVENT_QUEUED` is defined explicitly for the sake of clarity.
          // Treat queuing as any other store update to correctly dispatch events
          // that can be dispatched immediately.
          case EVENT_QUEUED:
          default:
            {
              var prevState = store.getState();
              var result = next(action);
              var nextState = store.getState();

              var queue = getQueue(nextState);
              var sendableAction = find(function (action) {
                var eventDefinition = events[action.type];

                if (eventDefinition) {
                  var event = eventDefinition(action, prevState, nextState);
                  return event != null;
                }
              }, queue);

              var sendableActionId = path(['meta', '@rushplay/analytics', 'queueId'], sendableAction);

              var skippedActions = omit([sendableActionId], queue);

              if (sendableAction) {
                store.dispatch(sendEvent(sendableActionId));
                store.dispatch(sendableAction);
              }

              if (skippedActions) {
                forEach(function (action) {
                  var queueId = path(['@rushplay/analytics', 'queueId'], action.meta);
                  var skipCount = path(['@rushplay/analytics', 'skipCount'], action.meta);

                  if (skipCount === SKIP_LIMIT) {
                    var error = new Error(action.type + ' was skipped too many times');

                    onError(error);
                  }
                  store.dispatch(skipEvent(queueId));
                }, skippedActions);
              }

              return result;
            }
        }
      };
    };
  };
}
/* eslint-enable no-empty */

var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

/**
 * Gets btag
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} btag
 */
function getBtag(state) {
  return path(['btag'], state);
}

/**
 * Gets affiliate id part of btag
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} affiliateId
 */
function getAffiliateId(state) {
  var btag = getBtag(state) || '';

  var _btag$split = btag.split('_'),
      _btag$split2 = _slicedToArray(_btag$split, 1),
      affiliateId = _btag$split2[0];

  return affiliateId;
}

/**
 * Gets affiliate tag part of btag
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} affiliateTag
 */
function getAffiliateTag(state) {
  var btag = getBtag(state) || '';

  var _btag$split3 = btag.split('_'),
      _btag$split4 = _slicedToArray(_btag$split3, 2),
      affiliateTag = _btag$split4[1];

  return affiliateTag;
}

/**
 * Gets click_id
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} click_id
 */
function getClickId(state) {
  return path(['click_id'], state);
}

/**
 * Gets referral_id
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} referral_id
 */
function getReferralId(state) {
  return path(['referral_id'], state);
}

/**
 * Gets sub_id
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} sub_id
 */
function getSubId(state) {
  return path(['sub_id'], state);
}

/**
 * Gets utm_campaign
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} utm_campaign
 */
function getUtmCampaign(state) {
  return path(['utm_campaign'], state);
}

/**
 * Gets utm_medium
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} utm_medium
 */
function getUtmMedium(state) {
  return path(['utm_medium'], state);
}

/**
 * Gets utm_source
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} utm_source
 */
function getUtmSource(state) {
  return path(['utm_source'], state);
}

/**
 * Gets webmaster_id
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} webmaster_id
 */
function getWebmasterId(state) {
  return path(['webmaster_id'], state);
}

/**
 * Gets twclid - Twitter Click ID
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {string} twclid
 */
function getTwitterClickId(state) {
  return path(['twclid'], state);
}

/**
 * Gets analytics events queue
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {Array.<Object>}
 */
function getEventsQueue(state) {
  return reject(isNil, pathOr([], ['events'], state));
}

/**
 * Gets queued event by its ID
 * @memberof rushplay/analytics
 * @param {Object} state Analytics state
 * @returns {Array.<Object>}
 */
function getEvent(state, props) {
  return path(['events', props.id], state);
}

/**
 * Update action meta with event’s queue ID
 *
 * Used for further queue handling.
 *
 * @param {number} queueId
 * @param {Object} action Redux action object
 */
function injectEventQueueId(queueId, action) {
  return assocPath(['meta', '@rushplay/analytics', 'queueId'], queueId, action);
}

/**
 * Update action meta with skip counter
 *
 * @param {Object} action
 */
function injectEventSkipCount(action) {
  return assocPath(['meta', '@rushplay/analytics', 'skipCount'], 0, action);
}

/**
 * Update action meta with skip counter incremented by 1
 *
 * @param {Object} action
 */
function incrementEventSkipCount(action) {
  return over(lensPath(['meta', '@rushplay/analytics', 'skipCount']), inc, action);
}

function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments[1];

  switch (action.type) {
    case EVENT_QUEUED:
      {
        var events = pathOr([], ['events'], state);
        var queueId = events.length;

        return assocPath(['events'], append(injectEventSkipCount(injectEventQueueId(queueId, action.payload)), events), state);
      }

    case EVENT_SENT:
      {
        var _events = getEventsQueue(state);

        if (isEmpty(_events)) {
          return state;
        }

        return assocPath(['events', action.payload], null, state);
      }

    case EVENT_SKIPPED:
      {
        var event = path(['events', action.payload], state);

        if (event == null) {
          return state;
        }

        return assocPath(['events', action.payload], incrementEventSkipCount(event), state);
      }

    case BTAG_UPDATED:
      {
        return assocPath(['btag'], action.payload, state);
      }

    case CLICK_ID_UPDATED:
      {
        return assocPath(['click_id'], action.payload, state);
      }

    case REFERRAL_ID_UPDATED:
      {
        return assocPath(['referral_id'], action.payload, state);
      }

    case SUB_ID_UPDATED:
      {
        return assocPath(['sub_id'], action.payload, state);
      }

    case TWITTER_CLICK_ID_UPDATED:
      {
        return assocPath(['twclid'], action.payload, state);
      }

    case UTM_CAMPAIGN_UPDATED:
      {
        return assocPath(['utm_campaign'], action.payload, state);
      }

    case UTM_MEDIUM_UPDATED:
      {
        return assocPath(['utm_medium'], action.payload, state);
      }

    case UTM_SOURCE_UPDATED:
      {
        return assocPath(['utm_source'], action.payload, state);
      }

    case WEBMASTER_ID_UPDATED:
      {
        return assocPath(['webmaster_id'], action.payload, state);
      }

    case CLEARED:
      {
        return {};
      }

    default:
      {
        return state;
      }
  }
}

export { queueEvent, sendEvent, skipEvent, authenticate, selectAmount, selectPaymentMethod, selectCryptoCurrency, completeTransaction, deposit, pageview, playForFun, openGame, bonus, register, registrationStarted, updateBtag, updateClickId, updateReferralId, updateWebmasterId, updateSubId, updateUtmCampaign, updateUtmMedium, updateUtmSource, updateTwitterClickId, clear, eventQueuingMiddleware, AUTHENTICATED, CRYPTO_CURRENCY_SELECTED, DEPOSIT_AMOUNT_SELECTED, DEPOSIT_PAYMENT_METHOD_SELECTED, DEPOSIT_TRANSACTION_COMPLETED, DEPOSITED, PAGE_VIEWED, REGISTERED, BONUS_CLAIMED, REGISTRATION_STARTED, PLAY_FOR_FUN, OPEN_GAME, BTAG_UPDATED, CLICK_ID_UPDATED, REFERRAL_ID_UPDATED, SUB_ID_UPDATED, TWITTER_CLICK_ID_UPDATED, UTM_CAMPAIGN_UPDATED, UTM_MEDIUM_UPDATED, UTM_SOURCE_UPDATED, WEBMASTER_ID_UPDATED, CLEARED, EVENT_QUEUED, EVENT_SKIPPED, EVENT_SENT, reducer, getBtag, getAffiliateId, getAffiliateTag, getClickId, getReferralId, getSubId, getUtmCampaign, getUtmMedium, getUtmSource, getWebmasterId, getTwitterClickId, getEventsQueue, getEvent, BTAG, CLICK_ID, REFERRAL_ID, SUB_ID, TWITTER_CLICK_ID, UTM_CAMPAIGN, UTM_MEDIUM, UTM_SOURCE, WEBMASTER_ID };
